/** Known LaunchDarkly flag keys, centralised so the string literal lives in
 *  one place across the codebase. Keys are kebab-case to match the LD project
 *  convention (cf. `custom-system-roles`). */

/** Gates the intake submission-logic runtime (requester page navigator + the
 *  submit rework it builds toward). Default **off** in all environments and
 *  treated as off when absent, so the incremental epic can land slice by slice
 *  without changing the live single-shot requester flow until it is
 *  feature-complete. Epic #2698, slice S1 #2705. */
export const INTAKE_SUBMISSION_LOGIC_FLAG = "intake-submission-logic";

/** Gates the Backoffice admin portal (NG-252, epic; shell NG-3425). Server side,
 *  every `/api/backoffice/*` and `/auth/backoffice/*` view returns 404 while it is
 *  off, evaluated for the staff member (anonymously before sign-in). That is the
 *  real gate. The Backoffice bundle (`apps/frontend/backoffice/`) also reads it,
 *  through its own anonymous LaunchDarkly client, to show "Backoffice isn't
 *  available" instead of the shell: cosmetic only. Created manually in the LD UI;
 *  defaults **off** and is treated as off when absent, or when the bundle has no
 *  LaunchDarkly client ID. */
export const BACKOFFICE_APP_FLAG = "backoffice-app";

/** Gates every goals surface: the sidebar Strategy section and its Goals item,
 *  the `/:slug/goals` management page, and goals as a project field (the picker
 *  in project settings → General, the Goals row in the team project side panel,
 *  and the reference prefetch the project overview loader makes for it).
 *
 *  Goals reached every environment as soon as NG-60/NG-67 landed, but edit and
 *  delete are still open, so the flag is what decides where a half-built surface
 *  is visible: **on** in local/development/staging, **off** in production, and
 *  treated as off when absent. NG-153.
 *
 *  Reports read this flag too (NG-622): the report builder's column picker is
 *  populated from the BACKEND field catalog, which knows nothing about
 *  LaunchDarkly, so the Goals columns are filtered client-side here rather than
 *  being absent from the response. Who may *see* goal data is a separate,
 *  server-side question answered by `goal.read`. */
export const GOALS_FLAG = "goals";

/** Gates the program metrics surfaces: the Progress / Health / Effort columns on
 *  the workspace Programs list, and the KPI band (Projects Overdue, Program
 *  Progress, Hours Logged + the status/health/effort donuts) on the program
 *  overview page. NG-2229 rides it too: sortable program tables, the alignment
 *  strip and the Details sheet.
 *
 *  Only the *read* surfaces are gated. The rollup table, its staleness marking
 *  and the 5-minute drain keep running with the flag off, so the numbers are
 *  already correct the moment it is switched on rather than starting at an
 *  em-dash for a drain cycle; the `metrics` block on the program serializer is
 *  additive and inert when nothing renders it.
 *
 *  Created manually in the LD UI; defaults **off** and is treated as off when
 *  the flag is absent. NG-285 (epic), NG-289 / NG-290 (the gated surfaces). */
export const PROGRAM_METRICS_FLAG = "program-metrics";

/** Gates the Cornerstone (CSOD) LMS integration, and only that.
 *
 *  NG-942 S1.5 said Cornerstone is never visible while 360Learning is off,
 *  because 360Learning built the connection, catalog, link / unlink and results
 *  surfaces Cornerstone reuses. That rule is now satisfied for good: 360Learning
 *  shipped to everyone and its flag was retired (NG-2072), so there is nothing
 *  left to order this key against. Cornerstone is always named in the catalog
 *  and connects only while this key is on.
 *
 *  Read it through `connector-catalog.ts`, where it is Cornerstone's
 *  `connectFlag`, rather than at a call site: `connectorsFor` and
 *  `availabilityOf` are what turn it into Available or Planned.
 *
 *  Created in LaunchDarkly on 2026-09-14 and treated as off when absent. NG-941
 *  (epic), NG-942 (this flag).
 *
 *  What it gates, since NG-2242: whether Cornerstone is Available in the Integration
 *  Library, which is what opens its connector page (the route refuses it while
 *  Planned), its Connect form, and its row on Installed. With it on, the LMS sentences
 *  with no connection in hand (the Delivery and picker empty states, the
 *  recommended-content copy) stop naming 360Learning, because they cannot tell which
 *  vendor they are about (`workspaceLmsVendor` in `lib/integrations/lms-vendors.ts`).
 *  The backend connects Cornerstone only where `CORNERSTONE_API_SCOPES` is set, and
 *  refuses the form otherwise, so an environment needs both. */
export const LMS_INTEGRATION_CORNERSTONE_FLAG = "lms-integration-cornerstone";

/** Gates the private draft-requests surface in the requester fill flow
 *  (NG-702, epic NG-657): the "Save draft" action while filling a form, the
 *  resume-from-draft path (`?draft=` on the fill route), routing submit of a
 *  resumed draft through the draft-submit endpoint, and the "Your drafts"
 *  section on the request-type picker. The backend draft endpoints are ungated
 *  and RBAC-guarded — the flag only decides where the UI surfaces. Defaults
 *  **off** and is treated as off when absent, so the lane lands dark and turns
 *  on per environment. */
export const PRIVATE_DRAFT_REQUESTS_FLAG = "private-draft-requests";
/** Gates the "Create project" button on a request's Linked Projects tab
 *  (NG-701): the Request Owner's entry into the standard project-creation
 *  dialog, pre-linked to the request. Only the button is gated — the tab, the
 *  table and the backend convert endpoints are ungated and RBAC-guarded, and
 *  the `can_create_project` server verdict (owner-only) still decides who could
 *  ever see it. Defaults **off** and is treated as off when absent, so the
 *  slice lands dark and turns on per environment once the epic's lanes are
 *  reviewed together. NG-701 (epic NG-657). */
export const REQUEST_LINKED_PROJECTS_CREATE_FLAG = "request-linked-projects-create";
/** Gates the persisted table sort and column filters (NG-517): the `sorting` and
 *  `columnFilters` slices of the per-browser table preferences, on both the
 *  hook path (`useTableSorting` / `useTableColumnFilters`, which every
 *  client-sorted list surface takes) and the store path (the ProjectStore,
 *  IntakeStore and ReportStore reactions that persist their server-sorted
 *  lists).
 *
 *  Only those two slices are gated. `columnOrder`, `columnVisibility` and
 *  `columnSizing` (NG-451) keep persisting for everyone — they predate this work
 *  and are not part of it.
 *
 *  With the flag off a table sorts and filters exactly as it did before NG-517:
 *  the controls still work, the choice just does not survive a reload, and
 *  nothing is read from or written to localStorage for those two slices. So the
 *  work lands dark and turns on per environment. Created in the LD project
 *  `next-gen` on 2026-09-02, **off** in all four environments (local,
 *  development, staging, production), and treated as off when absent. NG-517. */
export const PERSISTED_TABLE_PREFS_FLAG = "persisted-table-prefs";

/** Gates the whole Microsoft Teams notification-channel surface (epic NG-886): the
 *  workspace settings → Integrations entry that consents the tenant, the
 *  per-person "connect my Teams" control, and the category picker that narrows
 *  which notifications go out.
 *
 *  Off is the only safe default here, because the thing on the other side of the
 *  flag is a message in somebody's Teams chat: a half-wired channel does not
 *  degrade quietly the way a hidden page does, it pushes cards. So the flag is
 *  **off** everywhere and treated as off when absent, and the backend substrate
 *  (NG-952) lands first, dark and unreachable — no connection row exists, so the
 *  emit path finds nothing to deliver to whatever the flag says. NG-952 (this
 *  slice), epic NG-886.
 *
 *  Read by the Integration Library route and its Microsoft Teams card (NG-1096) —
 *  the first call sites. The knip dark-flag exemption this export carried while the
 *  key was registered a PR ahead of its UI is therefore deleted: left behind, it
 *  would let a genuinely dead flag key sit in this file unnoticed. */
export const MS_TEAMS_INTEGRATION_FLAG = "ms-teams-integration";

/** Gates every customer-facing surface of the Custom System Roles epic
 *  (NG-1233) as its slices land: the System Role vocabulary (NG-1246), the
 *  permission-gated Roles surface (NG-1254), the System Roles table, create
 *  modal and edit page (NG-1255/1256/1259/1257/1271), enforcement following the
 *  toggles (NG-1258), the Members/Access/export changes (NG-1247/1249/1250),
 *  and the removal of the user menu's Workspace Invitations item (NG-1248).
 *  NG-1248 also hid the profile Danger Zone behind it; NG-935 removed that
 *  for everyone, so the profile no longer reads this flag.
 *
 *  One key for the whole epic rather than one per story, because the slices are
 *  only coherent together: the rename alone renames a page whose Create button
 *  is still inert, and the create modal alone would ship under the old
 *  vocabulary. Off is the right default because the epic changes what a role
 *  *means* — the backend now derives a member's License Tier from their System
 *  Role — so the surface stays dark until the whole of NG-1233 passes QA and
 *  product picks the moment.
 *
 *  Its first consumer is the vocabulary overlay, so this key needs no
 *  ahead-of-its-call-site exemption tag: `SystemRoleVocabularySync` imports
 *  this key today.
 *
 *  Created manually in the LD UI; defaults **off** in all environments and is
 *  treated as off when absent. NG-1233 (epic), NG-1246 (first slice). */
export const CUSTOM_SYSTEM_ROLES_FLAG = "custom-system-roles";

/** Gates relationship eligibility settings (NG-2603): the "Can be assigned as"
 *  switches on a System Role's editor (NG-3896) and the per-member overrides on the
 *  Access tab (NG-3897). Off, the role editor and Access tab are unchanged, and Goal
 *  Lead stays a permission toggle. Turn it on only in a workspace where the backend's
 *  `relationship-eligibility-reads` flag is on, or a member override is saved and
 *  nothing reads it. Created manually in the LD UI; defaults **off** and is treated
 *  as off when absent. */
export const ROLE_ELIGIBILITY_SETTINGS_FLAG = "role-eligibility-settings";

/** Gates the System Role Permissions tab's tools (epic NG-2603): the "All" switch on
 *  each Available Area card that turns the whole Area on or off (NG-3920), and the
 *  search and filter over the list (NG-3921). Off, the tab is one switch per
 *  permission, as before. Defaults **off** and is treated as off when absent. */
export const ROLE_PERMISSIONS_TAB_TOOLS_FLAG = "role-permissions-tab-tools";

/** Gates the Rebalance entry points on the Capacity People view: the action on
 *  an over-allocated person's row and the matching action on an over-allocation
 *  risk chip. The proposal dialog and its backend endpoint remain in place, so
 *  turning the flag back on restores the existing workflow without a deploy.
 *  Defaults **off** and is treated as off when absent, so Rebalance stays hidden
 *  while product decides when to make it available again. */
export const CAPACITY_REBALANCE_FLAG = "capacity-rebalance";

/** Gates the deferral of the Projects page's reference banks (NG-1566): with
 *  the flag on, the six banks that feed only the table's cells and the filter
 *  popover's facets — members, teams, programs, goals, custom fields and
 *  category fields — are fetched when the table is the active layout or the
 *  filter panel is first opened, instead of on mount whatever the layout. Off,
 *  all six go out on mount exactly as they did before, so a board open still
 *  pays for six responses it never reads.
 *
 *  It gates the filter popover's loading state with them, and has to: deferral
 *  is what opens the gap the placeholders fill. With the flag off the banks are
 *  in flight from mount, so the panel shows **no placeholder at all** — which is
 *  what it does on `main` today, and what makes this dark rather than merely
 *  quieter.
 *
 *  Deliberately NOT gated: the category-values cache added alongside it. That
 *  one stops a refetch of data the page already holds and changes nothing a user
 *  can see, so it is the "internals / perf" case that ships unflagged.
 *
 *  A flag for a perf change because the deferral has one visible consequence —
 *  the filter panel fills in rather than arriving full — and product decides
 *  when that is on. Created in the LD project `next-gen` on 2026-09-22, **off**
 *  in all four environments (local, development, staging, production), and
 *  treated as off when absent. NG-1566. */
export const PROJECTS_DEFERRED_REFERENCE_BANKS_FLAG = "projects-deferred-reference-banks";

/** Gates the login screen's new "SSO unavailable" state (NG-1370, FR26/FR28,
 *  epic NG-1367). Today, an SSO-enabled domain whose identity provider is
 *  broken or unconfigured silently bounces the user back to the email-entry
 *  screen with no visible explanation (the SSO-initiate failure redirects
 *  with `?error=`, a query param the login screen's error banner has never
 *  read — it only reads `?error_code=`). This flag decides which of two
 *  read-only backend facts to render for that case: off, "SSO" (today's
 *  auto-redirect into the same silent failure, pixel-identical to current
 *  behavior); on, "SSO_UNAVAILABLE" (a support-contact banner instead).
 *
 *  No backend behavior is gated — `EmailCheckEndpoint` always reports the
 *  factual live/unavailable state; this flag only decides which one the
 *  frontend acts on. Pre-auth, so it evaluates against an anonymous LD
 *  context rather than a signed-in user's. Created manually in the LD UI;
 *  defaults **off** and is treated as off when the flag is absent. */
export const SSO_LOGIN_ROUTING_FLAG = "sso-login-routing";

/** Gates reading the `?error=` query param the SSO redirect flow
 *  (`error_redirect` in `apps/backend/cognota/sso/views_auth.py`) sends on a
 *  refused login — `sso_account_not_linked` (the NG-1686 cross-tenant guard),
 *  `oidc_provider_error`, `saml_provider_error`, `sso_error`,
 *  `sso_not_configured`, `domain_not_configured`, `email_required`,
 *  `invalid_email`, `sso_sign_in_expired`, `sso_email_missing`. That param is a different scheme from `?error_code=`
 *  (`authErrorHandler`'s numeric codes) and `auth-root.tsx` has never read it,
 *  so today every one of these bounces the user back to a blank, unexplained
 *  email form. Off reproduces that silent failure byte-for-byte; on renders
 *  the matching `auth.error.<slug>` copy through the existing error banner,
 *  with the email form still visible underneath as the way to sign in another
 *  way. No backend behavior is gated — the backend has sent this param
 *  unconditionally since the NG-1686 hotfix regardless of this flag.
 *
 *  Created manually in the LD UI; defaults **off** and is treated as off when
 *  the flag is absent. NG-1710. */
export const SSO_ERROR_MESSAGING_FLAG = "sso-error-messaging";

/** Gates surfacing the backend's specific rejection message on the SAML/OIDC
 *  provider forms (NG-1927) when NG-1702's single-active-provider rule (FR21)
 *  rejects activating a second identity provider. Off: the existing generic
 *  "Failed to create/update provider" toast for every error, unchanged. On:
 *  that one documented rejection shows its own message ("Another identity
 *  provider is already active for this workspace. Disable it before
 *  activating another."), telling the admin how to remediate instead of
 *  leaving them to guess; every other error still falls back to the generic
 *  toast. Created manually in the LD UI; defaults **off** and is treated as
 *  off when the flag is absent. Do NOT restore the pre-NG-1702 silent
 *  auto-disable behavior regardless of this flag's state — that reject-vs-
 *  auto-switch call was made deliberately on NG-1702. */
export const SSO_PROVIDER_ERROR_DETAIL_FLAG = "sso-provider-error-detail";

/** Gates every new-model behaviour of the shared identity-provider epic
 *  (NG-3319): shared domains, the per-workspace sign-in proofs and checks,
 *  password scope, SSO for a named workspace and for people in several
 *  workspaces, the workspace switch, sign-in links, IdP selection, the
 *  just-in-time role, and the linking and "Choose how to sign in" screens.
 *  Off is exactly today's sign-in. The backend reads the same key
 *  (`SSO_SHARED_IDENTITY_PROVIDERS` in `cognota/core/launchdarkly/flags.py`)
 *  for one fixed anonymous context, because sign-in has no workspace and often
 *  no user, so target it by environment only, never by user or workspace, or
 *  the screens and the API disagree. `sso-connection-reads` must be on in an
 *  environment first. Created manually in the LD UI; defaults **off** and is
 *  treated as off when absent. NG-3325. Among its screens: linking a provider
 *  to other workspaces on the SSO settings page (NG-3555), and the URLs a
 *  connection on its own URLs is registered under (NG-3557), and the role and teams for new people from SSO (NG-3559). */
export const SSO_SHARED_IDENTITY_PROVIDERS_FLAG = "sso-shared-identity-providers";

/** Gates filling an SSO connection from its identity provider's metadata on
 *  the SSO settings page (NG-3888): a SAML metadata file, its text or its URL,
 *  or an OIDC issuer's discovery document. Typing every field stays as today,
 *  and is the fallback when a fill fails. Off, the page is exactly today's
 *  typed form and the backend's metadata endpoint answers 404. The backend
 *  reads the same key (`SSO_CONNECTION_METADATA_FILL` in
 *  `cognota/core/launchdarkly/flags.py`) for one fixed anonymous context, so
 *  target it by environment only. Created manually in the LD UI; defaults
 *  **off** and is treated as off when absent. */
export const SSO_CONNECTION_METADATA_FILL_FLAG = "sso-connection-metadata-fill";

/** Gates the refreshed sign-in and Settings › SSO screens (NG-3319): one
 *  sign-in layout, step header, button style and option row, consistent copy
 *  in English and French (NG-3968, NG-3969, NG-3971), and the SSO settings
 *  page as one page of panels with one copy field and plain domain rows
 *  (NG-3972, NG-3973, NG-3974). Off is exactly today's screens. Its screens
 *  restyle ones `sso-shared-identity-providers` adds, so that flag must be on
 *  in an environment first. Frontend only. Created manually in the LD UI;
 *  defaults **off** and is treated as off when absent. */
export const SSO_EXPERIENCE_REFRESH_FLAG = "sso-experience-refresh";

/** Gates the read-only state on a record the viewer can open but not edit
 *  (NG-1872): a collapsed "You can read this … but not change it." banner with a
 *  "Why?" disclosure naming the viewer's System Role, over fields that render as
 *  disabled controls rather than as plain text or live inputs. One key for the
 *  epic's four surfaces (Requests NG-1875, Projects NG-1876, Programs NG-1877,
 *  Work items NG-1878), because the PRD's point is one consistent pattern: half
 *  the surfaces explaining themselves and half not is the confusion it removes.
 *
 *  It ships alongside Custom System Roles, which is what makes "can view but not
 *  edit" common, so product turns it on with that; merged is not released.
 *  Created manually in the LD UI; defaults **off** in all four environments
 *  (local, development, staging, production) and is treated as off when absent. */
export const READ_ONLY_INDICATORS_FLAG = "read-only-indicators";

/** Gates the autosave status pill under the event description (NG-2833). The
 *  rich-HTML description round trip itself is the BUG FIX and ships unflagged —
 *  editing an event description no longer escapes its formatting into literal
 *  tags — so only the NEW visible indicator (the Saving/Saved/Save failed pill
 *  under the editor) rides this flag and merges dark, per the rule that any
 *  visible change ships behind a LaunchDarkly flag.
 *
 *  Created manually in the LD UI; defaults **off** in all environments and is
 *  treated as off when absent. */
export const EVENT_DESCRIPTION_SAVE_STATUS_FLAG = "event-description-save-status";

/** Gates the workspace-template SHARING vocabulary (NG-1355): the save dialog's
 *  "Shared with GTM" / "Private" choice, and the same two values on the templates
 *  table's tag and its Scope filter. A private template also loses its "Mark as
 *  default for new workspaces" action, which the backend refuses for one.
 *
 *  Off: exactly what shipped before it, "Global" / "Restricted", with
 *  "Restricted" posted as the deprecated alias the backend stores as `global`.
 *
 *  The dialog posts `private` with the flag on, which a backend older than
 *  NG-1355's sharing change refuses, so the flag must not go on in an
 *  environment until that backend is deployed there. Created manually in the
 *  LD UI on 2026-09-29, once that backend was on dev: **on** in development and
 *  staging, **off** in production and local, and treated as off when absent. */
export const WORKSPACE_TEMPLATE_SHARING_FLAG = "workspace-template-sharing";

/** Gates the Team Lead create affordances (NG-2176, under NG-1992): the create
 *  controls #5842's backend enabled but never surfaced — "New Project" on the
 *  Projects list and on a team's Projects tab, and "Create team" on My Teams —
 *  for callers who hold the create verb only at a led team's scope, not at the
 *  workspace scope.
 *
 *  Only the SHOWING of previously hidden controls rides this flag. The
 *  companion tightening — hiding controls whose write the backend refuses (a
 *  sub-team create for a non-lead, the "No parent" option for a caller without
 *  a workspace-level team create, the delivery-team options a caller cannot
 *  create under) — ships unflagged, the #5966 hide-only precedent: it removes
 *  a predictable authorization failure, not a capability.
 *
 *  The capability probes behind it (`project.create` / `team.create` with no
 *  `team_id`, answered from the workspace scope ∪ the caller's led teams) are
 *  ungated backend behaviour: they only ever feed an affordance, never
 *  authorize a write. Created manually in the LD UI; defaults **off** and is
 *  treated as off when the flag is absent. */
export const TEAM_LEAD_CREATE_FLAG = "team-lead-create";

/** Gates the Start of week control on Profile → Preferences (NG-2275). No view
 *  reads `Profile.start_of_the_week` yet, so turn it on only once they do.
 *  Hiding writes nothing, so users' saved choices survive. Created manually in
 *  the LD UI; defaults **off** and is treated as off when absent. */
export const START_OF_WEEK_FLAG = "start-of-week";

/** Gates the **Public holidays** card on Workspace Settings → Calendars
 *  (NG-2277). Inverted: the card had shipped, and off is what HIDES it, since
 *  Holiday calendars cover the same need. Only the UI is gated — the
 *  `holiday-calendar/me/` endpoint stays live, and a feed already generated keeps
 *  reducing capacity. Created manually in the LD UI; treated as off when absent. */
export const PUBLIC_HOLIDAYS_FLAG = "public-holidays";

/** Gates a person's own calendar connections (NG-2278): the "User calendar
 *  connections" card on workspace settings → Calendars, with its Connect Google /
 *  Connect Outlook buttons, sync and disconnect actions, and the toast shown on
 *  the OAuth redirect back. Ported from Plane and not validated, so it stays dark
 *  for the LA release. Holiday calendars and domain-wide sync are not behind it.
 *
 *  Only the UI is gated: the `users/me/calendar-*` endpoints stay live, and the
 *  30-minute OAuth sync keeps running for anyone already connected. Defaults
 *  **off** and is treated as off when absent. */
export const CALENDAR_CONNECTIONS_FLAG = "calendar-connections";

/** Gates the SAML Single Logout copy on the SSO provider form (NG-2047): the
 *  SLO URL hint and the sharpened Logout URL hint that explain the SLO-vs-
 *  plain-redirect precedence. The SLO behaviour itself only activates when a
 *  deployment configures SP signing keys, but the copy is visible UI and so
 *  merges dark. Created manually in the LD UI; defaults **off** and is treated
 *  as off when absent. */
export const SAML_SINGLE_LOGOUT_COPY_FLAG = "saml-single-logout-copy";

/** Gates Workspace Settings → Integrations → Webhooks: the nav entry, the list
 *  page and the webhook detail page (NG-2330). The settings tab registry carries
 *  it, so the layout hides the entry and redirects a deep link, for both
 *  `webhooks` and `webhooks/:webhookId`.
 *
 *  This one hides a page that already shipped, rather than holding back a new
 *  one. Only work-item events are ever delivered (`work_items/handlers.py` is the
 *  sole producer), so four of the editor's five event checkboxes never fire
 *  (NG-591), and prod had no deliveries and no webhook edits in the 30 days to
 *  2026-10-02. The backend is not gated: the API stays, and existing webhooks
 *  keep receiving work-item events.
 *
 *  Created in LaunchDarkly **off** in every environment (Dan, 2026-10-02); the
 *  hermetic e2e stack forces it on so the page keeps its coverage. Product
 *  decides when it returns. Treated as off when absent, which hides the page. */
export const WEBHOOKS_FLAG = "webhooks-settings";

/** A plain click on another project's bar in the Plan work Gantt stays on
 *  the tab instead of navigating to that project's Overview (NG-1026). The
 *  scheduler's in-progress, unsaved plan is lost by the navigation today, so
 *  on, the unscored bar's link swallows primary-button clicks; cmd/ctrl/
 *  middle-click (open in a new tab), keyboard activation, the name column's
 *  link and a scored bar's priority breakdown are untouched. Off, a bar click
 *  navigates the current tab, as before. Treated as off when absent. */
export const PLAN_WORK_GANTT_BAR_NO_NAVIGATE_FLAG = "plan-work-gantt-bar-no-navigate";

/** Gates the fix for a nested (sub-task / sub-session) row selected in a
 *  project's Work items table (NG-3116, parent NG-2509). Today the page builds
 *  its bulk-action eligibility lookup from the ROOT list rows only; a selected
 *  nested row lives in its parent's lazily read children and so reads as unknown,
 *  and the fail-closed gates hide everything but Export. With the flag on, the
 *  page fetches each expanded parent's children with the new opt-in
 *  `expand_permissions` query param (so each child carries its own per-row
 *  delete/edit verdict, like a top-level row) and folds those children into the
 *  same eligibility lookup, so a nested selection offers the full bulk bar —
 *  Set status, Assignees, Archive, Delete, Export — exactly where the caller can
 *  actually perform each action. Off, the lookup stays root-only and a nested
 *  selection keeps today's Export-only bar. No backend authorization changes:
 *  the flag only surfaces the verdicts the write endpoints already enforce.
 *  Created manually in the LD UI; defaults **off** and is treated as off when
 *  absent. */
export const NESTED_WORK_ITEM_BULK_ACTIONS_FLAG = "nested-work-item-bulk-actions";

/** Events and planning polish (NG-1022 / NG-1023 / NG-1024). On, Plan work
 *  offers Week and Month only, falling back to Month if Day was selected when
 *  the flag changed (NG-1022): it schedules whole-day projects, so Day's hourly
 *  columns add no detail. It also shows weekdays by default, with a Week-only
 *  View weekends switch (NG-1024). Off or absent, all three periods and all
 *  seven days, with no switch. */
export const EVENTS_POLISH_FLAG = "events-polish";

/** Gates the Utilization section on an event (NG-3401, epic NG-3383): the Hours
 *  field on the Edit Event page and in the Quick Edit drawer, where a user sets
 *  how much of the event counts against each assignee's capacity. The backend
 *  counts Hours unflagged, but nothing in the app sets them while this is off, so
 *  every number stays what it was. Treated as off when absent. */
export const EVENT_UTILIZATION_HOURS_FLAG = "event-utilization-hours";
/** Gates the bulk time-off CSV import on Workspace Settings › Time Off
 *  (NG-3406, epic NG-3404). On, the page offers an Import button beside Export,
 *  opening the shared CSV wizard against the NG-3405 backend endpoint; off or
 *  absent, the page is unchanged and the endpoint, though served, is unreachable
 *  from the UI. Created manually in the LD UI; defaults **off**. */
export const TIME_OFF_CSV_IMPORT_FLAG = "time-off-csv-import";

/** Gates the work-item CSV/XLSX import on a project's Work Items list (NG-3504,
 *  epic NG-3433). On, the list's header offers an Import button beside "Create Work
 *  Item", opening the shared CSV wizard against the project-scoped NG-3498/3501/3503
 *  backend endpoints; off or absent, the list is unchanged and the endpoints, though
 *  served, are unreachable from the UI. Created manually in the LD UI; defaults
 *  **off** and is treated as off when absent. */
export const PROJECT_WORK_ITEMS_IMPORT_FLAG = "project-work-items-import";

/** Gates the work-item CSV/XLSX import on a project TEMPLATE's Table tab (NG-3509,
 *  epic NG-3435). On, the template editor's toolbar offers an Import button beside
 *  "Create work item", opening the shared CSV wizard against the template-scoped
 *  NG-3506/3508 backend endpoints; off or absent, the toolbar is unchanged and the
 *  endpoints, though served, are unreachable from the UI. Created manually in the LD
 *  UI; defaults **off** and is treated as off when absent. */
export const TEMPLATE_WORK_ITEMS_IMPORT_FLAG = "template-work-items-import";

/** Gates the Edit Event page rebuilt like the Task page (NG-3402, epic
 *  NG-3384): Comments in a drawer opened from the header, the two-column
 *  layout, and the event's fields in an Overview panel at the side. Off, the
 *  current Edit Event page, unchanged. The Quick Edit drawer and the Timeline
 *  tab are the same either way. Treated as off when absent. */
export const EVENT_PAGE_PARITY_FLAG = "event-page-parity";

/** Kill switch for the Home team filter and column on the workspace Tasks and
 *  Events lists (NG-3453), ON in every environment since 2026-10-23. On, the
 *  Filter menu offers Home team on every layout and the Table shows a Home team
 *  column, read from each row's own `home_team`; off, neither appears and the
 *  CSV export never asks for the column. Read with a fallback of ON, so the
 *  feature stays up when LaunchDarkly is unreachable or disabled (local, CI) and
 *  before it answers. The backend's filter param and row field have their own
 *  default-on kill switch, `work-item-home-team-query`. */
export const WORK_ITEM_HOME_TEAM_FLAG = "work-item-home-team";

/** The Delivery team filter and column on the workspace Tasks and Events lists
 *  (NG-3457, NG-3458). A release gate: on, the Filter menu offers Delivery team
 *  on every layout and the Table offers a Delivery team column from Display;
 *  off, neither appears and no request names them. Treated as off when absent.
 *  The backend's filter param and row field sit behind their own default-on kill
 *  switch, `work-item-delivery-team-query`. */
export const WORK_ITEM_DELIVERY_TEAM_FLAG = "work-item-delivery-team";

/** Kill switch for the Home team column and filter on the workspace Requests list
 *  (NG-3462), ON in every environment. On, the table shows a Home team column read
 *  from each row's own `home_team`, its header filters by it, and the board's
 *  Filter menu offers it; off, none of them appear and the list never sends
 *  `home_team_ids`. Read with a fallback of ON, so the feature stays up when
 *  LaunchDarkly is unreachable or disabled (local, CI) and before it answers. The
 *  backend's filter param and row field have their own default-on kill switch,
 *  `request-home-team-query`. */
export const REQUEST_HOME_TEAM_FLAG = "request-home-team";

/** Gates the workspace type badge (NG-3418, epic NG-252): Sandbox, Trial or
 *  Internal next to the workspace name in the workspace switcher and the command
 *  palette. Server side the same key gates the Backoffice change-type endpoint
 *  (404 while off). Customer production shows nothing and Demo keeps its "(Demo)"
 *  label, which still reads `is_demo`. Created manually in the LD UI; defaults
 *  **off** and is treated as off when absent. */
export const WORKSPACE_TYPE_FLAG = "backoffice-workspace-type";

/** Gates the workspace email-suppression banner (NG-3410, epic NG-252): workspace
 *  Admins see "Email notifications are turned off for this workspace." while the
 *  workspace's `email_notifications_suppressed` is on. Server side the same key
 *  gates the Backoffice toggle (404 while off); the suppression itself follows the
 *  stored setting and is NOT gated. Created manually in the LD UI; defaults
 *  **off** and is treated as off when absent. */
export const WORKSPACE_EMAIL_SUPPRESSION_FLAG = "backoffice-email-suppression";

/** Gates the product side of Backoffice temporary access (NG-3417, epic NG-252): the banner a
 *  Cognota support identity sees while it works in a customer's workspace (with its countdown and
 *  "Leave workspace"), and the "Cognota Support" tag on comment and activity authors. Server side
 *  the same key gates the Backoffice routes and the handoff, and is a kill switch: off, every
 *  request by a support identity is refused. The handoff page and "Access ended" page are not
 *  gated here, because the server is what sends a person to them. Created manually in the LD UI;
 *  defaults **off** and is treated as off when absent. */
export const BACKOFFICE_TEMPORARY_ACCESS_FLAG = "backoffice-temporary-access";

/** Gates Intercom support chat (NG-3452, epic NG-3449): "Chat with us" in the
 *  user menu and everything that loads Intercom. Off, NextGen loads nothing from
 *  Intercom and sends it nothing, and the menu item isn't there. Evaluated per
 *  person and workspace; a workspace it reads off for ends that tab's chat.
 *  Created manually in the LD UI, available to client-side SDKs; defaults
 *  **off** and is treated as off when absent. */
export const INTERCOM_MESSENGER_FLAG = "intercom-messenger";

/** Gates the Digital Teammates settings and consent screens (NG-2622, WS2-F of
 *  epic NG-2211): the admin's Workspace settings → Digital Teammates tab, where a
 *  teammate is switched on or off for the workspace, and the member's Profile →
 *  Digital Teammates tab, where a person turns their own teammate on (consenting
 *  to its current version) or off. Both open the same "View full permissions"
 *  dialog.
 *
 *  Frontend only: the four `/api/workspaces/{slug}/teammates/` endpoints are
 *  additive and stay live, and with the flag off nothing calls them. Created
 *  manually in the LD UI; defaults **off** and is treated as off when absent, so
 *  it merges dark. Product decides when it goes on. */
export const DIGITAL_TEAMMATES_SETTINGS_FLAG = "digital-teammates-settings";

/** Gates the owner's activity log for their digital teammates (NG-3850, WS4-A of
 *  epic NG-2211): the "View activity" link on the person's own teammate in Profile →
 *  Digital Teammates, and the sheet it opens listing what the teammate did in their
 *  name, run by run, with its cost. It sits inside that profile tab, so it shows
 *  only when `digital-teammates-settings` is on as well.
 *
 *  Frontend only: the two `/teammates/{teammate}/mine/activity/` endpoints are
 *  additive and owner-only, and with the flag off nothing calls them. Created
 *  manually in the LD UI; defaults **off** and is treated as off when absent, so it
 *  merges dark. Product decides when it goes on, including whether members should
 *  see the cost in dollars. */
export const DIGITAL_TEAMMATES_ACTIVITY_FLAG = "digital-teammates-activity";

/** Backoffice workspace lifecycle controls (NG-3418). Enforcement follows stored status.
 */
export const WORKSPACE_LIFECYCLE_FLAG = "backoffice-workspace-lifecycle";

/** Gates Project Automation's first release (M1, epic NG-3494): the "Automations" area
 *  of the System Roles editor ("View automations", "Edit automations", NG-3680) and the
 *  Settings › Operations › Automations tab, the workspace's automation defaults
 *  (NG-3529). Off, both are hidden; the permissions still sit on every Admin and
 *  Standard role. The later releases' flags only act while this one is on.
 *
 *  Frontend only. Created manually in the LD UI; defaults **off** and is treated as off
 *  when absent, so it merges dark. Product decides when it goes on. */
export const PROJECT_AUTOMATION_WORKSPACE_FLAG = "project-automation-workspace";

/** Gates Project Automation's second release (M2, epic NG-3494): the team Automations
 *  pages. For now that is the "Automations" box in Settings › General › Team Lead
 *  permissions (NG-3680, NG-3637), shown only while this flag AND
 *  {@link PROJECT_AUTOMATION_WORKSPACE_FLAG} are on, since the box decides who reaches
 *  pages that arrive in M2.
 *
 *  Frontend only. Created manually in the LD UI; defaults **off** and is treated as off
 *  when absent, so it merges dark. Product decides when it goes on. */
export const PROJECT_AUTOMATION_TEAM_PAGES_FLAG = "project-automation-team-pages";

/** Gates Project Automation's third release (M3, epic NG-3494): the work item rules.
 *  Off, the "Project defaults › Work items" section of the Automations editor is
 *  hidden ("Close parent when its last sub-item completes", NG-3524, and its two
 *  siblings, NG-3525 and NG-3526), since the engine runs none of them until it is on,
 *  and so are the Project Settings › Automations tab (NG-3688) and "Archive closed
 *  work items" (NG-3521), since the nightly archive job sweeps no work item until it
 *  is on. Acts only while {@link PROJECT_AUTOMATION_WORKSPACE_FLAG} is on.
 *
 *  Keep the key in sync with the backend's ``PROJECT_AUTOMATION_WORK_ITEMS``
 *  (``cognota/core/launchdarkly/flags.py``): the engine and the archive job read the
 *  same flag before running these rules, so the page and the server agree. Created
 *  manually in the LD UI; defaults **off** and is treated as off when absent, so it
 *  merges dark. Product decides when it goes on. */
export const PROJECT_AUTOMATION_WORK_ITEMS_FLAG = "project-automation-work-items";

/** "Expand all / Collapse all" on a project's Work items Table (NG-3731). Off, every
 *  parent still opens one at a time, as today. On, the Table's view-options row
 *  offers the toggle, and expanding reads every loaded parent's subtasks in one
 *  request (`GET .../projects/{id}/sub-work-items/`, additive and inert with nothing
 *  calling it).
 *
 *  Frontend only. Created manually in the LD UI; defaults **off** and is treated as off
 *  when absent, so it merges dark. Product decides when it goes on. */
export const WORK_ITEM_TABLE_EXPAND_ALL_FLAG = "work-item-table-expand-all";

/** Gates editing a project's process stage by hand (NG-3867, epic NG-3864): the
 *  pin/revert affordance on the Projects table's Stage column and on the project
 *  Overview's Stage row, which lets a lead pin ``current_stage`` to a chosen
 *  stage (freezing it from work-item derivation) or revert it to auto. Off, both
 *  surfaces stay read-only exactly as today.
 *
 *  Only the EDITING affordance rides the flag. The read-only pin indicator — the
 *  pin badge on an already-pinned project — ships to everyone, so a project a
 *  lead pinned (or the seeder pinned) reads as pinned whatever this flag says;
 *  that is the "outcome of an action made visible" case, not a new surface. The
 *  `POST/DELETE .../stage-override/` endpoints are ungated and RBAC-guarded
 *  (`project.edit`), so the flag only decides where the UI offers the edit.
 *
 *  Created manually in the LD UI; defaults **off** and is treated as off when
 *  absent, so it merges dark. Product decides when it goes on. */
export const MANUAL_PROJECT_STAGE_OVERRIDE_FLAG = "manual-project-stage-override";

/** The dual "Create & Open" / "Create" footer on the standalone New Project
 *  dialog (NG-3600), bringing it to parity with the request→project convert
 *  flow (`IntakeConvertModal`), which already offers both. Off, the dialog keeps
 *  today's single "Create Project" button; on, it shows a secondary "Create"
 *  (creates and stays on the list) and a primary "Create & Open" (creates and
 *  navigates to the new project's overview).
 *
 *  Applies only to the standalone create flow. Bridge mode (creating a project
 *  from a request, `intakeSubmissionId` set) stays refresh-in-place and keeps
 *  the single button whatever this flag says, preserving NG-701.
 *
 *  Frontend only. Created manually in the LD UI; defaults **off** and is treated
 *  as off when absent, so it merges dark. Product decides when it goes on. */
export const PROJECT_CREATE_AND_OPEN_FLAG = "project-create-and-open";

/** Gates the enriched "Add to project" member picker (NG-3817): the search box
 *  above the member list and the third line on each row (the member's job role
 *  followed by their teams, truncated with the full list on hover). Off, the
 *  dialog is exactly today's — a checkbox list of name + email with no search;
 *  on, the list filters live across name, email, team name and job role. No
 *  backend change: the job role and teams already ride the workspace member
 *  bank payload (NG-1776), and the search filters the loaded list client-side.
 *
 *  Frontend only. Created manually in the LD UI; defaults **off** and is treated
 *  as off when absent, so it merges dark. Product decides when it goes on. */
export const PROJECT_MEMBER_PICKER_SEARCH_FLAG = "project-member-picker-search";

/** Gates the projects list "Last activity" column + sort (NG-3767). The column
 *  reads `last_activity_at` — the last user-made change to a project or anything
 *  in it — which the backend serves unflagged beside `updated_at`; this flag only
 *  decides whether the column and its sort option appear. Frontend only. Created
 *  manually in the LD UI; defaults **off** and is treated as off when absent, so
 *  it merges dark. Product decides when it goes on. */
export const PROJECT_LAST_ACTIVITY_FLAG = "project-last-activity";
/** The per-field "Move to page…" / "Duplicate to page…" actions in the Form
 *  Workflow builder's Edit Page modal (NG-3856). That modal scopes the draft to
 *  one page, so without this the two submenus have no destinations and render
 *  inert (they highlight but never open). On, the modal offers the workflow's
 *  other pages as destinations and moves / duplicates the field there through the
 *  workflow converter, not ``intakeStore.save()``.
 *
 *  The legacy multi-page intake builder is unaffected: it has every page in the
 *  draft and keeps moving / duplicating through the store regardless of this flag.
 *
 *  Frontend only. Created manually in the LD UI; defaults **off** and is treated
 *  as off when absent, so it merges dark. Product decides when it goes on. */
export const WORKFLOW_MOVE_FIELD_TO_PAGE_FLAG = "workflow-move-field-to-page";

/** Retires the legacy Request Type from the dashboards and the requester's form
 *  cards (NG-3931, epic NG-3935). Request Category replaced it in June: the form
 *  builder stopped setting it, so only older forms and their requests carry one.
 *  On, the dashboard filter menu stops offering "Request type", the widget
 *  editor stops offering it as an x-axis, group-by or table column, and the
 *  Start a request cards stop picking their icon from it. Anything already
 *  saved with it (an applied filter, a widget grouped by it, a table that
 *  lists it) keeps rendering and stays editable, so nothing breaks under a user.
 *
 *  Frontend only. Defaults **off** and is treated as off when absent, so it
 *  merges dark. Removing the flag is the first step of NG-3936. */
export const RETIRE_REQUEST_TYPE_FLAG = "retire-request-type";

/** Gates the Work item category dimension on Work Items dashboard widgets
 *  (NG-3947, Arvest): the X-axis and group-by option and the filter chip. It is
 *  the work item's OWN category, a task's Task category or an event's Event
 *  category, beside Project type, which is its project's.
 *
 *  Only the offer is gated. The backend serves the property and the
 *  `work_item_category` filter key whatever the flag says, because it has no
 *  LaunchDarkly client. So a widget saved while the flag was on still renders
 *  with it off: the frontend stops offering the option and the chip, nothing
 *  more. Defaults **off** and is treated as off when absent. */
export const DASHBOARD_WORK_ITEM_CATEGORY_FLAG = "dashboard-work-item-category";
